import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../UI/Button/Button";
import LoadingSpinner from "../../UI/LoadingSpinner/LoadingSpinner";
import { getUserSubscriptionPlans } from "../../../services/subscription-plan-service";
import { getCompleteSubscriptionData } from "../../../services/subscription-payment-service";
import styles from "./ManagePlans.module.css";

export default function ManagePlans() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAutoRenewEnabled = (plan) => {
    const status = String(plan?.status || "").toLowerCase();
    const isActive = status === "active" || status === "paid";
    return Boolean(plan?.autoRenew) && isActive;
  };

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getUserSubscriptionPlans();

      if (response.success && Array.isArray(response.data)) {
        setPlans(response.data);
      } else {
        setPlans([]);
      }
    } catch (err) {
      setError(err.message || t("billingPage.managePlans.errorDescription"));
      console.error("Error fetching subscription plans:", err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) {
      const currentDate = new Date();
      return currentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      const currentDate = new Date();
      return currentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  const formatAmount = (amount, currency = "EUR") => {
    if (amount === null || amount === undefined || amount === "")
      return t("billingPage.managePlans.na");
    try {
      const numAmount = Number(amount);
      if (isNaN(numAmount)) return t("billingPage.managePlans.invalidAmount");
      const displayAmount = numAmount;
      let symbol;
      if (currency === "USD") {
        symbol = "$";
      } else if (currency === "RON") {
        symbol = "RON";
      } else {
        symbol = "€";
      }
      return `${symbol} ${displayAmount.toFixed(2)}`;
    } catch {
      return t("billingPage.managePlans.invalidAmount");
    }
  };

  const isCancelAtPeriodEnd = (plan) =>
    plan?.cancelAtPeriodEnd === true ||
    plan?.cancelAtPeriodEnd === "true" ||
    plan?.cancelAtPeriodEnd === 1;

  const getStatusBadgeKey = (plan) => {
    const st = String(plan?.status ?? "")
      .toLowerCase()
      .trim();
    if (st === "canceling") return "canceling";
    if ((st === "active" || st === "paid") && isCancelAtPeriodEnd(plan)) {
      return "canceling";
    }
    return st;
  };

  const getStatusBadge = (plan) => {
    const key = getStatusBadgeKey(plan);
    const statusClasses = {
      paid: styles.statusPaid,
      pending: styles.statusPending,
      failed: styles.statusFailed,
      overdue: styles.statusOverdue,
      active: styles.statusActive,
      inactive: styles.statusExpired,
      canceling: styles.statusCanceling,
      "expiring soon": styles.statusExpiringSoon,
      expired: styles.statusExpired,
      canceled: styles.statusFailed,
      cancelled: styles.statusFailed,
    };
    return `${styles.statusBadge} ${
      statusClasses[key] || styles.statusDefault
    }`;
  };

  const getPlanStatusLabel = (plan) => {
    if (getStatusBadgeKey(plan) === "canceling") {
      return t("billingPage.managePlans.statusCancelingFriendly");
    }
    return plan.status || t("billingPage.managePlans.unknown");
  };

  const getPlanStatusTitle = (plan) => {
    if (getStatusBadgeKey(plan) === "canceling") {
      return t("billingPage.managePlans.stripeCancelScheduledHint");
    }
    return undefined;
  };

  const handleManagePlan = async (plan) => {
    if (!plan) return;

    const id = plan._id ?? plan.id ?? plan.projectId ?? "";
    const tier = plan.subscriptionPlan || plan.projectTier || plan.tier || "";
    const productName = plan.product || "";
    if (!id || !productName || !tier) {
      console.error("ManagePlans: missing id, product, or tier", {
        id,
        productName,
        tier,
      });
      alert(
        "We could not open subscription management for this plan. Missing required data (id/product/tier). Please refresh and try again.",
      );
      return;
    }

    const params = new URLSearchParams();
    params.set("id", id);
    params.set("product", productName);
    params.set("tier", tier);
    if (plan.projectTier) params.set("projectTier", plan.projectTier);
    if (plan.status) params.set("status", plan.status);
    if (plan.amount !== undefined && plan.amount !== null) {
      params.set("amount", String(plan.amount));
    }
    if (plan.currency) params.set("currency", plan.currency);
    if (plan.stripeSubscriptionId)
      params.set("stripeId", plan.stripeSubscriptionId);
    if (plan.autoRenew !== undefined && plan.autoRenew !== null) {
      params.set("autoRenew", String(Boolean(plan.autoRenew)));
    }

    const tierToken =
      String(tier).trim().split(/\s+/)[0]?.toLowerCase() || "basic";
    let tierSlug = "basic";
    if (tierToken.startsWith("premium")) tierSlug = "premium";
    else if (tierToken.startsWith("enterprise")) tierSlug = "enterprise";
    else if (tierToken.startsWith("basic")) tierSlug = "basic";

    const amount =
      plan.amount !== undefined && plan.amount !== null
        ? String(plan.amount)
        : "";

    let stripeSubscriptionId = plan.stripeSubscriptionId || "";
    try {
      const complete = await getCompleteSubscriptionData(id);
      if (complete?.success && complete?.data?.stripeSubscriptionId) {
        stripeSubscriptionId = complete.data.stripeSubscriptionId;
      }
    } catch {
    }

    const checkoutParams = new URLSearchParams({
      type: "subscription",
      planId: `${tierSlug}_monthly`,
      planName: String(tier),
      planTier: String(tier),
      amount,
      currency: plan.currency || "RON",
      description: `${tier} Plan Subscription`,
      productName: productName,
      existingProductName: productName,
      billingCycle: "Monthly",
      isSubscription: "true",
      isManagingMode: "true",
      existingPlanId: id,
      existingStripeSubscriptionId: stripeSubscriptionId,
      projectTier: plan.projectTier || String(tier),
      status: plan.status ? String(plan.status) : "",
    });
    if (plan.projectId) {
      checkoutParams.set("projectId", plan.projectId);
    }
    if (plan.renewalDate) {
      const rd =
        plan.renewalDate instanceof Date
          ? plan.renewalDate.toISOString()
          : String(plan.renewalDate);
      checkoutParams.set("renewalDate", rd);
    }
    navigate(`/checkout?${checkoutParams.toString()}`);
  };

  if (loading) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            {t("billingPage.managePlans.title")}
          </h2>
          <p className={styles.sectionDescription}>
            {t("billingPage.managePlans.loadingDescription")}
          </p>
        </div>
        <div
          style={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            {t("billingPage.managePlans.title")}
          </h2>
          <p className={styles.sectionDescription}>
            {t("billingPage.managePlans.errorDescription")}
          </p>
        </div>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p style={{ color: "#dc3545", marginBottom: "20px" }}>{error}</p>
          <Button onClick={fetchPlans}>
            {t("billingPage.managePlans.tryAgain")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            {t("billingPage.managePlans.title")}
          </h2>
          <p className={styles.sectionDescription}>
            {t("billingPage.managePlans.description")}
          </p>
        </div>
        <Button onClick={fetchPlans} style={{ padding: "12px 24px" }}>
          {t("billingPage.managePlans.refreshPlans")}
        </Button>
      </div>

      {!Array.isArray(plans) || plans.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon} aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 6h10M9 10h10M9 14h7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M7 4h-1a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M8 4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2H8V4z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className={styles.emptyStateMessage}>
            {t("billingPage.managePlans.noPlans")}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.billingTable}>
              <thead>
                <tr>
                  <th>{t("billingPage.managePlans.tableHeaders.planId")}</th>
                  <th>{t("billingPage.managePlans.tableHeaders.service")}</th>
                  <th>{t("billingPage.managePlans.tableHeaders.product")}</th>
                  <th>
                    {t("billingPage.managePlans.tableHeaders.renewalDate")}
                  </th>
                  <th>{t("billingPage.managePlans.tableHeaders.status")}</th>
                  <th>{t("billingPage.managePlans.tableHeaders.autoRenew")}</th>
                  <th>{t("billingPage.managePlans.tableHeaders.amount")}</th>
                  <th>{t("billingPage.managePlans.tableHeaders.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan, index) => {
                  if (!plan) return null;
                  return (
                    <tr
                      key={plan._id || index}
                      className={
                        index % 2 === 0 ? styles.rowEven : styles.rowOdd
                      }
                    >
                      <td className={styles.invoiceId}>
                        {plan.projectId || t("billingPage.managePlans.na")}
                      </td>
                      <td className={styles.serviceName}>
                        <div className={styles.serviceNameWrapper}>
                          <span
                            className={`${styles.serviceNameBadge} ${
                              styles[`service${plan.subscriptionPlan}`] || ""
                            }`}
                          >
                            {plan.subscriptionPlan ||
                              t("billingPage.managePlans.unknown")}{" "}
                            {t("billingPage.managePlans.planSuffix")}
                          </span>
                        </div>
                      </td>
                      <td className={styles.productName}>
                        {plan.product || t("billingPage.managePlans.na")}
                      </td>
                      <td>{formatDate(plan.renewalDate)}</td>
                      <td>
                        <span
                          className={getStatusBadge(plan)}
                          title={getPlanStatusTitle(plan)}
                        >
                          {getPlanStatusLabel(plan)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={
                            isAutoRenewEnabled(plan)
                              ? styles.autoRenewOn
                              : styles.autoRenewOff
                          }
                        >
                          {isAutoRenewEnabled(plan)
                            ? t("billingPage.managePlans.autoRenewOn")
                            : t("billingPage.managePlans.autoRenewOff")}
                        </span>
                      </td>
                      <td className={styles.amount}>
                        {formatAmount(plan.amount, plan.currency)}
                      </td>
                      <td>
                        <Button
                          type="button"
                          onClick={() => handleManagePlan(plan)}
                          style={{ padding: "8px 16px", fontSize: "14px" }}
                        >
                          {t("billingPage.managePlans.manage")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.cardsContainer}>
            {plans.map((plan, index) => {
              if (!plan) return null;
              return (
                <div key={plan._id || index} className={styles.planCard}>
                  <div className={styles.cardHeader}>
                    <div className={styles.planInfo}>
                      <div className={styles.planId}>
                        {plan.projectId || t("billingPage.managePlans.na")}
                      </div>
                      <div className={styles.productName}>
                        {plan.product || t("billingPage.managePlans.na")}
                      </div>
                    </div>
                    <span
                      className={`${styles.serviceNameBadge} ${
                        styles[`service${plan.subscriptionPlan}`] || ""
                      }`}
                    >
                      {plan.subscriptionPlan ||
                        t("billingPage.managePlans.unknown")}{" "}
                      {t("billingPage.managePlans.planSuffix")}
                    </span>
                  </div>

                  <div className={styles.cardBody}>
                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>
                        {t("billingPage.managePlans.cardLabels.renewalDate")}
                      </span>
                      <span className={styles.cardValue}>
                        {formatDate(plan.renewalDate)}
                      </span>
                    </div>

                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>
                        {t("billingPage.managePlans.cardLabels.status")}
                      </span>
                      <span
                        className={getStatusBadge(plan)}
                        title={getPlanStatusTitle(plan)}
                      >
                        {getPlanStatusLabel(plan)}
                      </span>
                    </div>

                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>
                        {t("billingPage.managePlans.cardLabels.autoRenew")}
                      </span>
                      <span
                        className={
                          isAutoRenewEnabled(plan)
                            ? styles.autoRenewOn
                            : styles.autoRenewOff
                        }
                      >
                        {isAutoRenewEnabled(plan)
                          ? t("billingPage.managePlans.autoRenewOn")
                          : t("billingPage.managePlans.autoRenewOff")}
                      </span>
                    </div>

                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>
                        {t("billingPage.managePlans.cardLabels.amount")}
                      </span>
                      <span className={styles.amount}>
                        {formatAmount(plan.amount, plan.currency)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.cardActions}>
                    <Button
                      type="button"
                      onClick={() => handleManagePlan(plan)}
                      style={{
                        padding: "10px 20px",
                        fontSize: "14px",
                        width: "100%",
                      }}
                    >
                      {t("billingPage.managePlans.managePlan")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
